// Wiederherstellungsprobe (docs/restore-probe.md): Stand je Kette und der
// laufende Durchgang. Quelle: GET /v2-api/backups/probe; der Live-Stand
// kommt waehrend eines Laufs zusaetzlich auf dem WS-Kanal "probe" (vom
// Modul selbst, hoechstens alle 0,75 s) -- die Ansicht zeigt, was gerade
// passiert, ohne zu pollen. Nach dem Lauf einmal nachladen: dann steht
// das Ergebnis in runs.result_json.
//
// Summary-Store nach docs/data-loading.md: Nutzer zaehlen, ein Abruf fuer
// alle, kein fetch im Page-$effect.

import { registerRevalidator } from "./revalidate.svelte";
import { subscribe as wsSubscribe } from "./ws-hub.svelte";

export interface ProbeOrt {
  host_id: string;
  host: string;
  ort: string;
  art: "zfs" | "verzeichnis";
}

export interface ProbePlan {
  kette: string;
  name: string;
  gruppe?: string;
  pruefbar: boolean;
  grund?: string;
  /** woher die Kette ihre Daten holt -- der Rechner, um den es im Ernstfall geht */
  quelle?: ProbeOrt;
  kopie?: ProbeOrt;
  /** diese Woche kommt die Kopie vom Wechsel-Rechner (Homeserver2) */
  wechsel?: boolean;
  referenz?: ProbeOrt;
  referenz_art?: "original" | "erste_kopie" | "kopie_selbst";
  weg?: "werkzeug" | "direkt" | "verzeichnis";
  ziel?: string;
  schritte: string[];
}

export type ProbeZustand = "wartet" | "laeuft" | "ok" | "fehler" | "entfaellt" | "nicht_geprueft" | "abgebrochen";

export interface ProbeSchritt {
  id: string;
  zustand: ProbeZustand;
  text?: string;
  anteil?: number;
  wert?: number;
  gesamt?: number;
  einheit?: "bytes" | "dateien";
  beginn?: string;
  ende?: string;
  dauer_s?: number;
}

export interface ProbeSeite {
  dateien: number;
  verzeichnisse: number;
  bytes: number;
  finger?: string;
  unlesbar?: number;
  dauer_s?: number;
}

export interface ProbeAbweichung {
  pfad: string;
  art: "fehlt" | "zusaetzlich" | "anders";
  felder?: string[];
}

export interface ProbeInhalt {
  pfad: string;
  art: "sqlite" | "gzip";
  ok: boolean;
  meldung?: string;
  bytes?: number;
}

export interface ProbeErgebnis extends ProbePlan {
  zustand: ProbeZustand;
  run_id?: string;
  beginn?: string;
  ende?: string;
  dauer_s?: number;
  stand?: string;
  stand_zeit?: string;
  versiegelt?: boolean;
  schluessel?: string;
  zurueck_bytes?: number;
  seite_kopie?: ProbeSeite;
  seite_original?: ProbeSeite;
  gleich: boolean;
  abweichungen?: ProbeAbweichung[];
  abweichungen_anzahl?: number;
  inhalt?: ProbeInhalt[];
  /** Datenbanken/Archive in Bibliotheks- und Bau-Verzeichnissen, inhaltlich ausgelassen */
  inhalt_ausgelassen?: number;
  gegenprobe?: { datei: string; erkannt: boolean };
  halte: ProbeSchritt[];
  fehler?: string;
  hinweis?: string;
}

export interface ProbeVerlauf {
  run_id: string;
  zustand: ProbeZustand;
  ende?: string;
  dauer_s?: number;
  gegenprobe?: boolean;
  fehler?: string;
}

export interface ProbeKette {
  plan: ProbePlan;
  letzter?: ProbeErgebnis;
  gegenprobe?: ProbeErgebnis;
  verlauf?: ProbeVerlauf[];
}

export interface ProbeLauf {
  run_id: string;
  beginn: string;
  ende?: string;
  aktiv?: string;
  ketten: ProbeErgebnis[];
}

export interface ProbeAufgabe {
  id: string;
  name: string;
  schedule?: string;
  zeitplan?: string;
  naechste?: string;
  enabled: boolean;
}

export interface ProbeWoche {
  woche: string;
  /** Montag der Woche (YYYY-MM-DD) */
  montag: string;
  /** je Kette das letzte Urteil der Woche */
  ketten: Record<string, ProbeZustand>;
}

export interface ProbeFund {
  art: "fehler" | "gegenprobe";
  kette: string;
  name: string;
  gruppe?: string;
  text: string;
  seit: string;
  zuletzt: string;
  laeufe: number;
  offen?: boolean;
  erkannt?: boolean;
  datei?: string;
}

export interface ProbeZuletzt {
  run_id: string;
  status: string;
  beginn: string;
  ende?: string;
  dauer_s?: number;
  bestanden: number;
  fehler: number;
  gegenprobe?: boolean;
}

export const backupProbe = $state<{
  loaded: boolean;
  error: string;
  aufgabe: ProbeAufgabe | null;
  lauf: ProbeLauf | null;
  ketten: ProbeKette[];
  zuletzt: ProbeZuletzt | null;
  wochen: ProbeWoche[];
  funde: ProbeFund[];
}>({ loaded: false, error: "", aufgabe: null, lauf: null, ketten: [], zuletzt: null, wochen: [], funde: [] });

let nutzer = 0;
let timer: ReturnType<typeof setTimeout> | undefined;

function plane(ms: number): void {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => void lade(), ms);
}

export async function lade(): Promise<void> {
  try {
    const r = await fetch("/v2-api/backups/probe", { credentials: "include" });
    if (r.ok) {
      const d = (await r.json()) as {
        aufgabe?: ProbeAufgabe;
        lauf?: ProbeLauf | null;
        ketten?: ProbeKette[];
        zuletzt?: ProbeZuletzt;
        wochen?: ProbeWoche[];
        funde?: ProbeFund[];
      };
      backupProbe.aufgabe = d.aufgabe ?? null;
      backupProbe.lauf = d.lauf ?? null;
      backupProbe.ketten = d.ketten ?? [];
      backupProbe.zuletzt = d.zuletzt ?? null;
      backupProbe.wochen = d.wochen ?? [];
      backupProbe.funde = d.funde ?? [];
      backupProbe.loaded = true;
      backupProbe.error = "";
    }
  } catch (e) {
    backupProbe.error = e instanceof Error ? e.message : String(e);
  } finally {
    // Ruhig: einmal pro Minute (der naechste Termin, ein Lauf von
    // anderswo). Waehrend eines Laufs traegt der WS-Kanal, der Abruf ist
    // nur das Netz darunter.
    if (nutzer > 0) plane(backupProbe.lauf ? 15_000 : 60_000);
  }
}

/** Gleich nach einem Start: der Lauf meldet sich erst nach dem Planen. */
export function stossAn(): void {
  if (nutzer > 0) {
    plane(400);
    setTimeout(() => nutzer > 0 && void lade(), 2500);
  }
}

// Der Live-Stand läuft immer mit (App.svelte, startBootStreams): eine
// laufende Probe steht in „läuft gerade" -- Statusleiste, Baum, Telefon
// --, auf jeder Seite. Nur das WS-Abo, kein Abruf: ohne Lauf kommt auf
// dem Kanal nichts.
let liveAbo: (() => void) | undefined;

function fertigeKetten(lauf: ProbeLauf | null): number {
  return lauf?.ketten.filter((k) => k.zustand === "ok" || k.zustand === "fehler" || k.zustand === "abgebrochen").length ?? 0;
}

export function startProbeLive(): void {
  if (liveAbo) return;
  // Fiel das End-Frame in eine Verbindungslücke (Laptop schlief, Deploy,
  // Netzwechsel), stand „Probe läuft" sonst auf jeder Seite weiter, bis
  // jemand die Probe-Seite öffnete. Beim Wiederverbinden und Zurückkommen
  // darum nachfragen -- aber nur, wenn es etwas zu klären gibt: eine Probe,
  // die hier noch läuft, oder die offene Seite. Ohne Lauf und ohne Seite
  // kein Abruf; eine Probe, die in der Lücke begann, meldet sich mit ihrem
  // nächsten Frame selbst.
  registerRevalidator(() => {
    if (nutzer > 0 || (backupProbe.lauf != null && backupProbe.lauf.ende == null)) void lade();
  });
  liveAbo = wsSubscribe("probe", (data: unknown) => {
    const d = data as { lauf?: ProbeLauf | null } | null;
    const lauf = d?.lauf ?? null;
    const warAktiv = backupProbe.lauf != null && backupProbe.lauf.ende == null;
    const vorher = fertigeKetten(backupProbe.lauf);
    backupProbe.lauf = lauf;
    // Ende des Laufs: das Ergebnis steht jetzt in den Läufen. Eine Kette
    // fertig: der Abruf liefert ihr Urteil schon mit (Kästchen, Datenstand,
    // Umfang) -- nicht erst nach allen zwölf.
    if (nutzer > 0 && (!lauf || lauf.ende) && (warAktiv || lauf?.ende)) plane(800);
    else if (nutzer > 0 && lauf && fertigeKetten(lauf) > vorher) plane(800);
  });
}

export function startBackupProbe(): void {
  nutzer++;
  if (nutzer > 1) return;
  startProbeLive();
  void lade();
}

export function stopBackupProbe(): void {
  nutzer = Math.max(0, nutzer - 1);
  if (nutzer > 0) return;
  if (timer) clearTimeout(timer);
  timer = undefined;
}
